import {naturalSort} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';

import * as analytics from 'analytics/Analytics';
import {PolicyStatement, Policy, getConditionDisplayString, Condition, ItemType} from 'auth/PolicyConstants';
import {IDENTITY_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {
  ActionOptionGroupNode,
  ActionOptionNode,
  ItemTypeOptionNode,
  PolicyTemplateNode,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import HelpButton from 'help/HelpButton';
import {miniSpacingLeft, miniSpacingRight, moderateSpacingBottom} from 'styles/Utilities';
import {GRAY_DARKER, EMERALD, GRAY_LIGHT} from 'ui-library/Colors';
import useOnMount from 'ui-library/hooks/useOnMount';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import {Cell, CellType, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {capitalize} from 'util/NameUtil';
import {assertNever} from 'util/Types';

export type EffectivePermissionsModalPolicy = Pick<Policy, 'name' | 'policyStatements'>;

export interface EffectivePermissionsModalProps {
  onRequestClose: () => void;
  policyTemplate: PolicyTemplateNode;
  policies: Array<EffectivePermissionsModalPolicy>;
  effectivePolicyStatements: Array<PolicyStatement>;
  resource: {
    type: string;
    id: string;
    name: string;
  };
  principalType: string;
}

export enum GrantedStatus {
  GRANTED = 'GRANTED',
  NOT_GRANTED = 'NOT_GRANTED',
  MIXED = 'MIXED',
}

function DescriptionHelpButton(props: {description: string}): JSX.Element {
  return (
    <HelpButton
      renderContent={() => <div className={css(styles.helpButtonContent)}>{props.description}</div>}
      iconClassName={css(styles.helpButtonIcon)}
    />
  );
}

// How many pixels to indent for nested policies
const INDENT_DEPTH = 20;

function renderActionOptionCell(
  actionOptionName: string,
  indent: number,
  description: string | null
): JSX.Element {
  const content = description ? (
    <div style={{marginLeft: `${indent * INDENT_DEPTH}px`}} className={css(styles.iconContainer)}>
      {[actionOptionName, <DescriptionHelpButton key="description" description={description} />]}
    </div>
  ) : (
    <div style={{marginLeft: `${indent * INDENT_DEPTH}px`}}>{actionOptionName}</div>
  );
  return <Cell className={css(styles.cell, styles.actionOptionCell)}>{content}</Cell>;
}

function createActionOptionsCells(
  actionOption: ActionOptionNode | ActionOptionGroupNode,
  indent: number = 0
): Array<JSX.Element> {
  if ('action' in actionOption) {
    return [renderActionOptionCell(actionOption.name, indent, actionOption.description)];
  } else {
    const childActionOptions = actionOption.actionOptions.flatMap((childActionOption) =>
      createActionOptionsCells(childActionOption, indent + 1)
    );
    return [
      renderActionOptionCell(actionOption.name, indent, actionOption.description),
      ...childActionOptions,
    ];
  }
}

function renderPolicyCell(
  status: GrantedStatus,
  isEffectivePermission?: boolean,
  conditions?: Set<Condition>
): JSX.Element {
  let content: ReactNode;
  switch (status) {
    case GrantedStatus.MIXED:
      content = (
        <BenchlingIcon
          className={css(isEffectivePermission ? styles.effectivePermissionCheckIcon : styles.policyCellIcon)}
          icon="minus"
        />
      );
      break;
    case GrantedStatus.NOT_GRANTED:
      content = undefined;
      break;
    case GrantedStatus.GRANTED:
      if (!conditions || conditions.has(Condition.ALWAYS)) {
        content = (
          <BenchlingIcon
            className={css(
              styles.checkIcon,
              isEffectivePermission ? styles.effectivePermissionCheckIcon : styles.policyCellIcon
            )}
            icon="check"
          />
        );
      } else {
        content = naturalSort([...conditions.keys()]).map((condition: Condition) => (
          <div key={condition} className={css(styles.conditionCellContainer)}>
            <BenchlingIcon
              className={css(
                styles.checkIcon,
                isEffectivePermission ? styles.effectivePermissionCheckIcon : styles.policyCellIcon
              )}
              icon="check"
            />
            {`${getConditionDisplayString(condition)}`}
          </div>
        ));
      }
      break;
    default:
      throw new Error(`Unimplemented status: ${assertNever(status)}`);
  }

  return <Cell className={css(styles.cell, styles.policyCell)}>{content}</Cell>;
}

function createPolicyCells(
  itemType: ItemType,
  actionOptions: Array<ActionOptionNode | ActionOptionGroupNode>,
  itemTypeActionToCondition: {[key: string]: Set<Condition>},
  isEffectivePermission?: boolean
): Array<JSX.Element> {
  const policyCells: Array<JSX.Element> = [];
  actionOptions.forEach((actionOption: ActionOptionNode | ActionOptionGroupNode) => {
    if ('action' in actionOption) {
      const itemTypeWithOverride = actionOption.itemTypeOverride || itemType;
      const conditionOrUndefined = itemTypeActionToCondition[`${itemTypeWithOverride}${actionOption.action}`];
      const grantedStatus = conditionOrUndefined ? GrantedStatus.GRANTED : GrantedStatus.NOT_GRANTED;
      policyCells.push(renderPolicyCell(grantedStatus, isEffectivePermission, conditionOrUndefined));
    } else if ('actionOptions' in actionOption) {
      const childPermissionCells = createPolicyCells(
        itemType,
        actionOption.actionOptions,
        itemTypeActionToCondition,
        isEffectivePermission
      );
      if (childPermissionCells.length > 0) {
        policyCells.push(<Cell className={css(styles.cell, styles.policyCell)} />, ...childPermissionCells);
      }
    }
  });

  return policyCells;
}

function renderPolicyColumn({
  itemTypeOptions,
  name,
  policyStatements,
  isEffectivePermission,
}: {
  itemTypeOptions: Array<ItemTypeOptionNode>;
  name: string;
  policyStatements: Array<PolicyStatement>;
  isEffectivePermission?: boolean;
}): JSX.Element {
  const itemTypeActionToCondition = policyStatements.reduce(
    (itemTypeActionToCondition, currentPolicyStatement) => {
      if (!itemTypeActionToCondition[currentPolicyStatement.itemType + currentPolicyStatement.action]) {
        itemTypeActionToCondition[currentPolicyStatement.itemType + currentPolicyStatement.action] =
          new Set();
      }
      itemTypeActionToCondition[currentPolicyStatement.itemType + currentPolicyStatement.action].add(
        currentPolicyStatement.condition
      );
      return itemTypeActionToCondition;
    },
    {} as {[key: string]: Set<Condition>}
  );
  const cells = itemTypeOptions.flatMap((itemTypeOption) => {
    const policyCells = createPolicyCells(
      itemTypeOption.itemType,
      itemTypeOption.actionOptions,
      itemTypeActionToCondition,
      isEffectivePermission
    );
    if (policyCells.length > 0) {
      return [
        <Cell cellTypeOverride={CellType.INLINE_HEADER} className={css(styles.cell, styles.policyCell)} />,
        ...policyCells,
      ];
    }
    return [];
  });

  return (
    <Column
      key={`${name}-${isEffectivePermission}`}
      header={<Cell className={css(styles.cell)}>{name}</Cell>}
      renderCell={(i: number) => cells[i]}
    />
  );
}

function createActionOptionCellsFromTemplate(itemTypeOptions: Array<ItemTypeOptionNode>): Array<JSX.Element> {
  return itemTypeOptions.flatMap((itemTypeOption) => {
    const itemTypeName = (
      <Cell className={css(styles.cell, styles.actionOptionCell)} cellTypeOverride={CellType.INLINE_HEADER}>
        {itemTypeOption.name}
      </Cell>
    );
    const actionItemLabels = itemTypeOption.actionOptions.flatMap((actionOption) =>
      createActionOptionsCells(actionOption)
    );
    if (actionItemLabels.length > 0) {
      return [itemTypeName, ...actionItemLabels];
    }
    return [];
  });
}

export default function EffectivePermissionsModal({
  onRequestClose,
  policyTemplate,
  policies,
  effectivePolicyStatements,
  resource,
  principalType,
}: EffectivePermissionsModalProps): JSX.Element {
  useOnMount(() => {
    analytics.trackEvent(IDENTITY_ANALYTICS_ACTIONS.OPENED_MODAL_EFFECTIVE_PERMISSIONS, {
      objectType: resource.type,
      objectId: resource.id,
    });
  });
  const actionOptionCells = createActionOptionCellsFromTemplate(policyTemplate.itemTypeOptions);
  return (
    <Modal
      onRequestClose={() => {
        analytics.trackEvent(IDENTITY_ANALYTICS_ACTIONS.CLOSED_MODAL_EFFECTIVE_PERMISSIONS, {
          objectType: resource.type,
          objectId: resource.id,
        });
        onRequestClose();
      }}
      fullHeight={true}
      fullWidth={true}
      scrollable={false}
      title={`${capitalize(resource.type)} Access Summary - ${resource.name}`}
    >
      <div className={css(styles.modalFlexbox)}>
        <p className={css(styles.infoText)}>
          The {principalType} can perform the following actions in this {resource.type.toLowerCase()} based on
          their effective permissions.
        </p>
        <p className={css(styles.infoText)}>
          Effective permissions are generated from all the access policies the {principalType} has been
          assigned for this specific {resource.type.toLowerCase()}. When a {principalType} is assigned more
          than one access policy, they are granted the most permissive action across all assigned policies.
        </p>
        <div className={css(styles.scrollContainer)}>
          <Table
            className={css(styles.table)}
            numRows={actionOptionCells.length}
            frozenHeader={true}
            overflowVisible={true}
            getRowClassName={() => css(styles.tableRow)}
          >
            <Column header="" renderCell={(i: number) => actionOptionCells[i]} />
            {renderPolicyColumn({
              name: 'Effective Permissions',
              policyStatements: effectivePolicyStatements,
              itemTypeOptions: policyTemplate.itemTypeOptions,
              isEffectivePermission: true,
            })}
            {policies.map((policy) =>
              renderPolicyColumn({
                name: policy.name,
                policyStatements: policy.policyStatements,
                itemTypeOptions: policyTemplate.itemTypeOptions,
              })
            )}
          </Table>
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  actionOptionCell: {
    minWidth: '350px',
  },
  cell: {
    boxShadow: 'none',
    fontSize: '14px',
    minWidth: '150px',
  },
  checkIcon: miniSpacingRight,
  conditionCellContainer: {
    alignItems: 'center',
    display: 'flex',
  },
  effectivePermissionCheckIcon: {
    color: EMERALD,
  },
  helpButtonContent: {
    whiteSpace: 'pre-wrap',
  },
  helpButtonIcon: miniSpacingLeft,
  iconContainer: {
    alignItems: 'center',
    display: 'flex',
  },
  infoText: {...moderateSpacingBottom, fontSize: '14px'},
  modalFlexbox: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  policyCell: {
    // border causes horizontal scrollbar due to pixel rounding
    boxShadow: `1px 0 0 0 ${GRAY_LIGHT}`,
  },
  policyCellIcon: {
    color: GRAY_DARKER,
  },
  // needed since modal is padded
  scrollContainer: {
    overflowY: 'scroll',
  },
  table: {
    borderRight: 0,
  },
  tableRow: {
    boxShadow: 'none',
  },
});
